import type { MetricsPositionDistribution, MetricsPositionIdentity, MetricsPositionReadiness } from "@rekonas/domain";

function object(value: unknown, fields: string[]): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value) || Object.keys(value).length !== fields.length || fields.some((key) => !(key in value))) throw new TypeError("Invalid metrics position fields");
  return value as Record<string, unknown>;
}
function number(value: unknown, max = 1e12): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0 || value > max) throw new TypeError("Invalid metrics position number");
  return value;
}
function identity(value: unknown): MetricsPositionIdentity {
  const v = object(value, ["data_version", "manifest_sha256", "stage_run_ref", "stage_sha256", "selection_revision", "context_run_ref", "context_sha256", "duration_seconds"]);
  for (const key of ["manifest_sha256", "stage_sha256", "context_sha256"]) if (typeof v[key] !== "string" || !/^[0-9a-f]{64}$/.test(v[key])) throw new TypeError("Invalid metrics position hash");
  for (const key of ["data_version", "stage_run_ref", "context_run_ref"]) if (typeof v[key] !== "string" || !v[key] || v[key].length > 120) throw new TypeError("Invalid metrics position binding");
  if (!Number.isSafeInteger(v.selection_revision) || number(v.selection_revision) < 0 || number(v.duration_seconds, 604800) === 0) throw new TypeError("Invalid metrics position identity");
  return value as MetricsPositionIdentity;
}
function recording(value: unknown): string {
  if (typeof value !== "string" || !value || value.length > 120) throw new TypeError("Invalid metrics position recording");
  return value;
}
export function parseMetricsPositionReadiness(value: unknown): MetricsPositionReadiness {
  const v = object(value, ["recording_ref", "available", "identity", "reason"]);
  recording(v.recording_ref);
  if (typeof v.available !== "boolean" || (v.reason !== null && (typeof v.reason !== "string" || v.reason.length > 1024))) throw new TypeError("Invalid metrics position readiness");
  if (v.available) { identity(v.identity); if (v.reason !== null) throw new TypeError("Invalid available metrics reason"); }
  else if (v.identity !== null || typeof v.reason !== "string") throw new TypeError("Invalid unavailable metrics identity");
  return value as MetricsPositionReadiness;
}
export function parseMetricsPositionDistribution(value: unknown): MetricsPositionDistribution {
  // JSON is already received under the gateway ceiling; independently bound the
  // parsed mobile payload too. No arrays are accepted through permissive casts.
  if (new TextEncoder().encode(JSON.stringify(value)).length > 2 * 1024**2) throw new TypeError("Metrics position size limit");
  const v = object(value, ["version", "algorithm", "convention", "recording_ref", "identity", "window_s", "bin_width_deg", "sensors"]);
  if (v.version !== "2.0" || v.algorithm !== "metrics-position-v1" || v.convention !== "neoma.position-floor-bins/1" || v.window_s !== 900 || v.bin_width_deg !== 1) throw new TypeError("Unsupported metrics position contract");
  recording(v.recording_ref);
  const pin = identity(v.identity), window = number(v.window_s), bins = Math.round(360 / number(v.bin_width_deg));
  const snapshots = Math.ceil(pin.duration_seconds / window);
  const sensors = object(v.sensors, ["head", "torso"]);
  const matrix = (rows: unknown, max: number, counts = false): number[][] => {
    if (!Array.isArray(rows) || rows.length !== snapshots || snapshots > 672) throw new TypeError("Invalid metrics position snapshots");
    let previous: number[] = Array(bins).fill(0);
    for (const row of rows) {
      if (!Array.isArray(row) || row.length !== bins) throw new TypeError("Invalid metrics position bins");
      row.forEach((item, index) => { if (number(item, max) < previous[index] || (counts && !Number.isSafeInteger(item))) throw new TypeError("Invalid cumulative metrics support"); });
      previous = row;
    }
    return rows as number[][];
  };
  for (const id of ["head", "torso"]) {
    if (sensors[id] === null) continue;
    const sensor = object(sensors[id], ["seconds", "spo2_sums", "spo2_counts"]);
    const seconds = matrix(sensor.seconds, 604800);
    let previous = 0;
    seconds.forEach((row, i) => {
      const total = row.reduce((a, b) => a + b, 0);
      if (total - previous > Math.min(window, pin.duration_seconds - i * window) + 1e-6) throw new TypeError("Invalid interval duration");
      previous = total;
    });
    if (sensor.spo2_sums === null && sensor.spo2_counts === null) continue;
    const sums = matrix(sensor.spo2_sums, 1e12), counts = matrix(sensor.spo2_counts, 1e12, true);
    sums.forEach((row, i) => row.forEach((sum, j) => {
      const delta = sum - (i ? sums[i - 1][j] : 0), n = counts[i][j] - (i ? counts[i - 1][j] : 0);
      if ((n === 0 && delta !== 0) || delta > 100 * n) throw new TypeError("Invalid SpO2 support");
    }));
  }
  return value as MetricsPositionDistribution;
}
